import React, { useEffect, useState, ReactNode } from 'react';

import { AlignJustify, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Sidebar from '../../components/layout/Sidebar';
import Topbar from '../../components/layout/Topbar';
import { getRoles, getPermissions } from '../../utils/tokenUtils';
import { FeedbackDrawerProvider } from '../../context/FeedbackDrawerContext';
import { ButtonLegendDrawerProvider } from '../../context/ButtonLegendDrawerContext';

interface Breadcrumb {
  label: string;
  href: string;
}

interface AdminLayoutProps {
  activeMenuItem: string;
  breadcrumbs: Breadcrumb[];
  allowedRoles: string[];
  children: ReactNode;
  requiredPermission?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  activeMenuItem,
  breadcrumbs,
  children,
  allowedRoles,
  requiredPermission
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roles, setRoles] = useState<string[] | null>(null);
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [previousPath, setPreviousPath] = useState<string>('/login');

  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== '/login') {
      setPreviousPath(location.pathname);
    }
  }, [location]);

  useEffect(() => {
    const userRoles = getRoles();
    const userPermissions = getPermissions();
    setRoles(userRoles);
    setPermissions(userPermissions);
  }, []);

  useEffect(() => {
    if (roles && permissions) {
      const hasRole = roles.some((role) => allowedRoles.includes(role));
      const hasPermission = !requiredPermission || permissions.includes(requiredPermission);

      if (!hasRole || !hasPermission) {
        // Try to go back in history first
        if (window.history.length > 2) {
          navigate(-1);
        } else {
          // If no history, try the stored previous path
          if (previousPath && previousPath !== location.pathname) {
            navigate(previousPath);
          } else {
            // If all else fails, go to dashboard
            navigate('/login');
          }
        }
      }
    }
  }, [roles, permissions, allowedRoles, requiredPermission, navigate, previousPath, location.pathname]);

  const toggleSidebar = (): void => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900 overflow-x-hidden">
      {/* Topbar */}
      <Topbar />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} activePage={activeMenuItem} />

        {/* Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden bg-gray-300 dark:bg-gray-900">
          {/* Breadcrumb with white background */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-300 dark:border-gray-600 px-4 py-3 h-12 flex items-center space-x-4">
            {/* Icon Button */}
            <button
              className="lg:hidden p-3 text-gray-500 dark:text-gray-300 rounded-lg focus:outline-none"
              onClick={toggleSidebar}
            >
              <AlignJustify className="w-6 h-6" />
            </button>

            {/* Breadcrumb Navigation */}
            <nav className="text-lg font-semibold text-gray-600 dark:text-gray-300">
              <ol className="inline-flex space-x-1">
                {breadcrumbs.map((breadcrumb, index) => (
                  <li
                    key={index}
                    className={`items-center ${index === 1 ? 'inline-flex' : 'hidden md:inline-flex'}`}
                  >
                    <a
                      href={breadcrumb.href}
                      className={`${index === breadcrumbs.length - 1
                        ? 'text-gray-400 dark:text-gray-500' // Lighter gray for the last breadcrumb
                        : 'text-gray-500 dark:text-gray-300 hover:underline'
                        }`}
                    >
                      {breadcrumb.label}
                    </a>
                    {index < breadcrumbs.length - 1 && (
                      <ChevronRight className={`w-4 h-4 text-gray-500 dark:text-gray-400 mx-1 ${index === 0 || index === 1 ? 'hidden md:inline' : ''}`} />
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden" data-main-container>
            <div className="max-w-full">
              <main className="min-w-0">
                <FeedbackDrawerProvider>
                  <ButtonLegendDrawerProvider>{children}</ButtonLegendDrawerProvider>
                </FeedbackDrawerProvider>
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
